import { flatMap } from 'lodash';
import { Component } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import {
  CompanyWeirdImpactContribution,
  Contribution,
  ProductWeirdImpactContribution,
} from '../../../../../../../common/model/input/contributions/types';
import { Dimension, ORDERED_IMPACTS_BY_DIMENSION, Valence } from '../../../../../../../common/model/types';
import { friendlyShortImpactTitle, friendlyValence } from '../../../../../../../common/utils';
import { ModalOptions } from '../../../../../../untidy/redux/modules/modal';
import { aiName } from '../../../../../../untidy/texts';
import { TextField } from '../../../../../generic/components/inputs/text-field';
import { themeDEPRECATED } from '../../../../../generic/global/styles';
import { replaceOrAddToArray } from '../../../../../generic/utilities/utils';
import {
  ContributionModalParagraph,
  ContributionModalTitle,
  FormRow,
  FullRow,
  Label,
  RemoveLink,
  SubLabel,
} from './common';
import { FormButton } from './form-button';
import { url as circleSelected } from './images/circle-selected.svg';
import { url as circleUnselected } from './images/circle-unselected.svg';

type ImpactStatus = 'missing' | 'too-small' | 'too-big' | 'should-not-exist';

interface WeirdImpactFormImpact {
  impactCode: string;
  valence: Valence;
  status?: ImpactStatus;
}

interface WeirdImpactFormValidImpact {
  impactCode: string;
  valence: Valence;
  status: ImpactStatus;
}

interface State {
  comment?: string;
  impacts?: WeirdImpactFormImpact[];
}

interface Props {
  targetType: 'company' | 'product';
  isSubmitting: boolean;
  submitContribution: (options: { target?: string; data: Contribution }) => void;
  options: ModalOptions;
}

const RadioGroupWrapper = styled.div`
  margin-top: ${themeDEPRECATED.rhythm(4)};
`;

function getColorForOptionProps(props: OptionProps) {
  if (props.noneSelected) {
    return themeDEPRECATED.colors.cyan();
  }
  if (props.selected) {
    return themeDEPRECATED.colors.pink();
  }
  return themeDEPRECATED.colors.grayWarm();
}

const OptionText = styled.div`
  ${themeDEPRECATED.fontSemiBold};
  ${themeDEPRECATED.fontSize(1)};
  padding-left: ${themeDEPRECATED.rhythm(2)};
  color: ${(props: OptionProps) => getColorForOptionProps(props)};
`;

interface OptionProps {
  noneSelected: boolean;
  selected: boolean;
}

const OptionSymbol = styled.div`
  flex-basis: 16px;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${themeDEPRECATED.rhythm(2.5)};
  img {
    display: block;
  }
`;

const QuestionOptions = styled.div`
  margin-top: ${themeDEPRECATED.rhythm(3.5)};
  align-items: center;
  display: block;
`;

const QuestionOptionRoot = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  & + & {
    margin-top: ${themeDEPRECATED.rhythm(3)};
  }
`;

export class WeirdImpactForm extends Component<Props, State> {
  public state: State = {};

  public render() {
    const {
      isSubmitting,
      options: { target, targetName, existingData },
      targetType,
    } = this.props;
    const { comment, impacts } = this.state;

    if (!existingData) {
      console.error(`Missing required metadata for ${targetType}`);
      return null;
    }
    const { revenue, impacts: currentScores } = existingData;

    if (!currentScores) {
      console.error('Missing required metadata for weird impact');
      return null;
    }

    const allImpacts = flatMap(
      ORDERED_IMPACTS_BY_DIMENSION[Dimension.ENVIRONMENT]
        .concat(ORDERED_IMPACTS_BY_DIMENSION[Dimension.HEALTH])
        .concat(ORDERED_IMPACTS_BY_DIMENSION[Dimension.SOCIAL])
        .concat(ORDERED_IMPACTS_BY_DIMENSION[Dimension.KNOWLEDGE]),
      impactCode => [
        { valence: Valence.POSITIVE, impactCode },
        { valence: Valence.NEGATIVE, impactCode },
      ],
    );
    const canSubmit = impacts && impacts.length > 0 && !impacts.find(impact => impact.status === undefined);

    // TODO: use new nice-radio group instead
    // of this copy-paste adaptation
    const radioOptions: Array<{ id: ImpactStatus; title: string }> = [
      {
        id: 'missing',
        title: 'It is missing even though it should be there',
      },
      {
        id: 'too-small',
        title: 'It is too small',
      },
      {
        id: 'too-big',
        title: 'It is too big',
      },
      {
        id: 'should-not-exist',
        title: `It is there, but it shouldn't be`,
      },
    ];

    return (
      <>
        <ContributionModalTitle>How does the net impact profile of {targetName} look weird?</ContributionModalTitle>
        <form
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          {impacts &&
            impacts.map(({ impactCode, status, valence }) => (
              <FormRow key={`${impactCode}-${valence}`}>
                <FullRow>
                  <Label>
                    {friendlyShortImpactTitle(impactCode)} ({friendlyValence(valence)}){' '}
                    <RemoveLink
                      onClick={() => {
                        this.setState(state => ({
                          impacts:
                            state.impacts &&
                            state.impacts.filter(i => i.impactCode !== impactCode || i.valence !== valence),
                        }));
                      }}
                    >
                      (remove)
                    </RemoveLink>
                  </Label>
                  <SubLabel>How is this impact off in the net impact profile?</SubLabel>
                  <RadioGroupWrapper>
                    <QuestionOptions>
                      {radioOptions.map(option => {
                        const selected = option.id === status;
                        const noneSelected = status === undefined;
                        const icon = selected ? circleSelected : circleUnselected;
                        return (
                          <QuestionOptionRoot
                            key={option.id}
                            onClick={() => {
                              const newImpact = {
                                impactCode,
                                valence,
                                status: option.id,
                              };
                              this.setState(state => ({
                                impacts: state.impacts
                                  ? replaceOrAddToArray(
                                      state.impacts,
                                      i => i.impactCode === impactCode && i.valence === valence,
                                      newImpact,
                                    )
                                  : [newImpact],
                              }));
                            }}
                          >
                            <OptionSymbol>
                              <img src={icon} />
                            </OptionSymbol>
                            <OptionText noneSelected={noneSelected} selected={selected}>
                              {option.title}
                            </OptionText>
                          </QuestionOptionRoot>
                        );
                      })}
                    </QuestionOptions>
                  </RadioGroupWrapper>
                </FullRow>
              </FormRow>
            ))}
          <FormRow>
            <FullRow>
              <Label>
                {impacts && impacts.length > 0
                  ? 'Want to comment on another impact?'
                  : "Choose an impact you'd like to comment on:"}
              </Label>
              <Select
                options={allImpacts
                  .filter(
                    i =>
                      !impacts ||
                      !impacts.find(
                        selectedImpact =>
                          i.impactCode.valueOf() === selectedImpact.impactCode && i.valence === selectedImpact.valence,
                      ),
                  )
                  .map(({ impactCode, valence }) => ({
                    value: `${impactCode}-${valence}`,
                    label: `${friendlyShortImpactTitle(impactCode)} (${friendlyValence(valence)})`,
                  }))}
                onChange={(options: any) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  const option = Array.isArray(options) ? options[0] : options;
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  if (option && option.value) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                    const [code, valence] = option.value.split('-');
                    this.setState(state => ({
                      impacts: [
                        ...(state.impacts || []),
                        {
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                          impactCode: code,
                          valence: valence as Valence,
                          // status: 'missing',
                        },
                      ],
                    }));
                  }
                }}
                value={null}
              />
            </FullRow>
          </FormRow>
          <FormRow>
            <TextField
              label="Anything else you want to share?"
              id="comment"
              type="textarea"
              value={comment}
              disabled={isSubmitting}
              metaLabel="Optional"
              onChange={value => {
                this.setState({ comment: value });
              }}
            />
          </FormRow>
          <ContributionModalParagraph style={{ marginTop: themeDEPRECATED.rhythm(5) }}>
            Please note that contributions will not automatically affect the data in the model, but will be processed
            and evaluated either by {aiName} or the Upright team.
          </ContributionModalParagraph>
          <FormRow>
            <FormButton
              type="submit"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                if (this.state.impacts) {
                  const contributionData = {
                    comment: this.state.comment,
                    impacts: this.state.impacts as WeirdImpactFormValidImpact[],
                    currentScores,
                  };

                  const data: CompanyWeirdImpactContribution | ProductWeirdImpactContribution =
                    targetType === 'company'
                      ? {
                          ...contributionData,
                          type: 'COMPANY_WEIRD_IMPACT',
                          // TODO: this only sends the first revenue's value. Change the format?
                          revenue:
                            revenue && revenue.length > 0 && revenue[0].value ? Number(revenue[0].value) : undefined,
                        }
                      : {
                          ...contributionData,
                          type: 'PRODUCT_WEIRD_IMPACT',
                        };

                  this.props.submitContribution({
                    data,
                    target,
                  });
                }
              }}
              style={{ width: '100%' }}
            >
              Submit
            </FormButton>
          </FormRow>
        </form>
      </>
    );
  }
}
